import React from 'react';
import styled from 'styled-components';
import { useFirebase } from '../../services/firebase';
import { LOADING_STATE } from '../../util/loading';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { checkmarkCircle, pencil, warning } from 'ionicons/icons';
import useIonicModalProps from '../../hooks/useIonicModalProps';
import PriorityCaseSettings from '../forms/PriorityCaseSettingsForm';
import { getDatabase } from 'firebase/database';
import { setPriorityInfo } from '../../services/buildfire/rdb/cases';
import Typography from '../common/Typography';
import moment from 'moment';

interface PriorityToolbarProps {}

const Container = styled.div`
  display: flex;
  border-top: 1px solid var(--ion-color-light);
  justify-content: space-between;
  align-items: center;
  padding-left: 16px;
  position: relative;
  box-shadow: 0 -4px 8px -4px rgba(0, 0, 0, 0.12);
  padding-bottom: var(--ion-safe-area-bottom, 0px);
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

function formatTime(time: string): string {
  return moment(time, 'HH:mm').format('h:mm A');
}

const PriorityToolbar: React.FC<PriorityToolbarProps> = (props) => {
  const { priorityCaseInfo, app } = useFirebase();
  const isLoading = priorityCaseInfo?.loadingState === LOADING_STATE.LOADING;
  const modalProps = useIonicModalProps();
  const [present] = useIonToast();
  return (
    <Container>
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <>
          <TextContainer>
            <Typography variant="secondary" style={{ flex: 1 }}>
              Priority Cases
            </Typography>
            <div>
              <Typography variant="body2" style={{ flex: 1 }}>
                {priorityCaseInfo?.enabled
                  ? `Enabled from ${formatTime(
                      priorityCaseInfo.businessStart!
                    )} - ${formatTime(priorityCaseInfo.businessEnd!)} CT`
                  : `Disabled`}
              </Typography>
            </div>
          </TextContainer>
          <IonButton id="edit-priority" fill="clear">
            <IonIcon icon={pencil} slot="icon-only" />
          </IonButton>
        </>
      )}

      <IonModal showBackdrop trigger="edit-priority" {...modalProps}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Edit Priority Case Settings</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={modalProps.onClose}>Close</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {priorityCaseInfo?.loadingState !== LOADING_STATE.LOADING && (
            <PriorityCaseSettings
              initialValues={{
                enabled: priorityCaseInfo?.enabled ?? false,
                businessStart: priorityCaseInfo?.businessStart ?? '08:00',
                businessEnd: priorityCaseInfo?.businessEnd ?? '16:00',
                price: priorityCaseInfo?.price ?? 2800,
                ...priorityCaseInfo,
              }}
              onSubmit={async (values) => {
                console.log('Saving priority case settings:', values);
                try {
                  const db = getDatabase(app);
                  await setPriorityInfo(db, values);
                  present({
                    message: 'Priority case settings saved successfully',
                    duration: 2000,
                    icon: checkmarkCircle,
                  });
                  modalProps.onClose();
                } catch (e) {
                  console.error(e);
                  present({
                    message: 'Failed to save priority case settings',
                    duration: 6000,
                    color: 'danger',
                    icon: warning,
                  });
                }
              }}
              onCancel={modalProps.onClose}
            />
          )}
        </IonContent>
      </IonModal>
    </Container>
  );
};

export default PriorityToolbar;
